/**
 * GoogleBusinessProfileConnect — dashboard card that lets the owner
 * connect their Google Business Profile and import location data
 * (name, address, phone, hours, website) from Google Maps into NoFee.
 *
 * UX flow:
 *   1) Card shows "Connect Google Business Profile" button when not yet
 *      connected, or "Connected as <email>" + "Choose location" CTA when
 *      already connected.
 *   2) Clicking Connect opens a centered popup that hits
 *      /api/google-gbp/connect → Google → /api/google-gbp/oauth2/callback.
 *      The backend's callback HTML posts a message back via
 *      window.opener.postMessage; we listen for { source: 'nofee-gbp-oauth' }.
 *   3) Once connected, we fetch /api/google-gbp/accounts then
 *      /api/google-gbp/locations?account=<name>. Owner picks ONE location
 *      → POST /api/google-gbp/import { location_name }. Backend patches
 *      the restaurant doc and we show a green confirmation toast listing
 *      the imported fields.
 *
 * Props:
 *   token: owner JWT
 *   onImported?: () => void   — called after a successful import so the
 *                                parent Dashboard can refetch restaurant.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe, Link2, LogOut, MapPin, CheckCircle2, Loader2, AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GoogleBusinessProfileConnect({ token, onImported }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [status, setStatus] = useState({ configured: false, connected: false, google_email: '' });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [importing, setImporting] = useState('');

  const refreshStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/google-gbp/status`, { headers });
      setStatus(r.data || {});
    } catch (_e) {
      setStatus({ configured: false, connected: false });
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const fetchAccounts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/google-gbp/accounts`, { headers });
      const list = r.data?.accounts || [];
      setAccounts(list);
      // Auto-pick the first account so the location list loads immediately.
      if (list.length && !selectedAccount) setSelectedAccount(list[0].name || '');
    } catch (err) {
      toast.error(`Could not list Google accounts: ${err.response?.data?.detail || err.message}`);
    }
  }, [headers, selectedAccount]);

  // Initial load
  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  // When status flips to connected, fetch accounts
  useEffect(() => {
    if (status.connected) fetchAccounts();
    else { setAccounts([]); setSelectedAccount(''); setLocations([]); }
  }, [status.connected, fetchAccounts]);

  // When an account is selected, fetch its locations
  useEffect(() => {
    if (!selectedAccount) { setLocations([]); return; }
    let cancelled = false;
    (async () => {
      setLoadingLocations(true);
      try {
        const r = await axios.get(`${API}/google-gbp/locations`, {
          headers, params: { account: selectedAccount },
        });
        if (!cancelled) setLocations(r.data?.locations || []);
      } catch (err) {
        if (!cancelled) toast.error(`Could not list locations: ${err.response?.data?.detail || err.message}`);
      } finally {
        if (!cancelled) setLoadingLocations(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAccount, headers]);

  // postMessage listener for the OAuth popup callback
  useEffect(() => {
    function onMessage(e) {
      if (!e?.data || e.data.source !== 'nofee-gbp-oauth') return;
      setConnecting(false);
      if (e.data.success) {
        toast.success(`Connected to Google${e.data.message ? ` as ${e.data.message}` : ''}`);
        refreshStatus();
      } else {
        toast.error(`Connection failed: ${e.data.message || 'unknown error'}`);
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [refreshStatus]);

  const handleConnect = async () => {
    if (!status.configured) {
      toast.error('Google Business Profile is not configured yet — please add credentials in backend .env.');
      return;
    }
    setConnecting(true);
    let authUrl;
    try {
      const r = await axios.post(`${API}/google-gbp/connect-init`, {}, { headers });
      authUrl = r.data?.auth_url;
    } catch (err) {
      setConnecting(false);
      toast.error(`Could not start connection: ${err.response?.data?.detail || err.message}`);
      return;
    }
    if (!authUrl) {
      setConnecting(false);
      toast.error('Google did not return an auth URL.');
      return;
    }
    const w = 520, h = 680;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(authUrl, 'nofee-gbp', `width=${w},height=${h},left=${left},top=${top}`);
    if (!popup) {
      setConnecting(false);
      toast.error('Browser blocked the popup. Allow popups for this site and try again.');
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/google-gbp/disconnect`, {}, { headers });
      toast.success('Disconnected from Google');
      refreshStatus();
    } catch (err) {
      toast.error(`Disconnect failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleImport = async (location) => {
    setImporting(location.name);
    try {
      const r = await axios.post(
        `${API}/google-gbp/import`,
        { location_name: location.name },
        { headers },
      );
      const fields = r.data?.imported_fields || [];
      toast.success(`Imported ${fields.length} field${fields.length === 1 ? '' : 's'} from Google`);
      if (typeof onImported === 'function') onImported();
    } catch (err) {
      toast.error(`Import failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setImporting('');
    }
  };

  // ───────────────────────────────────────── render
  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="gbp-loading">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!status.configured) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="gbp-not-configured">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-900">Google Business Profile not configured</h3>
            <p className="text-sm text-amber-800 mt-1">
              Ask the platform admin to add <code>GOOGLE_CLIENT_ID</code> + <code>GOOGLE_CLIENT_SECRET</code> in <code>backend/.env</code>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="gbp-card">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
          <Globe className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900">Google Business Profile</h3>
          <p className="text-sm text-slate-600">Import hours, address, phone and photos from your Google Maps listing.</p>
        </div>
      </div>

      {!status.connected ? (
        <div className="mt-3">
          <button
            type="button"
            onClick={handleConnect}
            disabled={connecting}
            className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50"
            data-testid="gbp-connect-btn"
          >
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            {connecting ? 'Opening Google…' : 'Connect Google Business Profile'}
          </button>
        </div>
      ) : (
        <>
          <div className="mt-3 flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2">
            <div className="flex items-center gap-2 text-sm text-emerald-800" data-testid="gbp-connected-status">
              <CheckCircle2 className="w-4 h-4" />
              Connected{status.google_email ? ` as ${status.google_email}` : ''}
            </div>
            <button
              type="button"
              onClick={handleDisconnect}
              className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
              data-testid="gbp-disconnect-btn"
            >
              <LogOut className="w-3 h-3" /> Disconnect
            </button>
          </div>

          {accounts.length > 1 && (
            <select
              className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              data-testid="gbp-account-select"
            >
              {accounts.map((a) => (
                <option key={a.name} value={a.name}>{a.accountName || a.name}</option>
              ))}
            </select>
          )}

          <div className="mt-3" data-testid="gbp-locations">
            <h4 className="text-sm font-semibold text-slate-800 mb-2">Your Google locations</h4>
            {loadingLocations ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            ) : locations.length === 0 ? (
              <p className="text-xs text-slate-500">
                No locations found under this Google account. Make sure your restaurant is verified on Google Maps.
              </p>
            ) : (
              <ul className="space-y-2">
                {locations.map((loc) => (
                  <li
                    key={loc.name}
                    className="flex items-start gap-3 rounded-lg border border-slate-200 p-3"
                    data-testid={`gbp-location-${loc.name}`}
                  >
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-slate-900 truncate">{loc.title || loc.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {[loc.storefrontAddress?.addressLines?.[0], loc.storefrontAddress?.locality, loc.storefrontAddress?.administrativeArea]
                          .filter(Boolean).join(', ')}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleImport(loc)}
                      disabled={importing === loc.name}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-gold-500 text-black text-xs font-semibold hover:bg-gold-400 disabled:opacity-50"
                      data-testid="gbp-import-btn"
                    >
                      {importing === loc.name ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                      {importing === loc.name ? 'Importing…' : 'Import to NoFee'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
